import { PrimaryMarketProject, ProjectGroupInfo } from 'api-client';

export type PrimaryMarketRow =
  | { kind: 'project'; project: PrimaryMarketProject }
  | { kind: 'group'; group: ProjectGroupInfo; members: Array<PrimaryMarketProject> };

/**
 * Сворачивает проекты одной группы в одну строку, сохраняя порядок, в котором их отдал API:
 * строка-группа занимает место своего первого участника и забирает остальных к себе.
 *
 * Два неочевидных момента:
 *  - проекты дедуплицируются по uuid: «Показать ещё» может повторно принести участника,
 *    который уже пришёл на предыдущей странице (группа способна попасть на стык страниц);
 *  - группа, от которой на вкладке остался один участник, разворачивается обратно в
 *    обычную строку — раскрывать там нечего.
 */
export const groupPrimaryMarketProjects = (
  projects: Array<PrimaryMarketProject>
): Array<PrimaryMarketRow> => {
  const rows: Array<PrimaryMarketRow> = [];
  const groupRowByUuid = new Map<
    string,
    { kind: 'group'; group: ProjectGroupInfo; members: Array<PrimaryMarketProject> }
  >();
  const seen = new Set<string>();

  projects.forEach((project) => {
    if (seen.has(project.uuid)) {
      return;
    }
    seen.add(project.uuid);

    if (!project.group) {
      rows.push({ kind: 'project', project });
      return;
    }

    const existing = groupRowByUuid.get(project.group.uuid);
    if (existing) {
      existing.members.push(project);
      return;
    }

    const groupRow = {
      kind: 'group' as const,
      group: project.group,
      members: [project],
    };
    groupRowByUuid.set(project.group.uuid, groupRow);
    rows.push(groupRow);
  });

  return rows.map((row) =>
    row.kind === 'group' && row.members.length === 1
      ? { kind: 'project', project: row.members[0] }
      : row
  );
};
